import { BUSINESS_TYPES, Current, FIELDS } from 'modules/cm/domain/Case/Applicant/Income/Current/Current';
import { CaseData } from 'modules/cm/api/cases/types';
import mappings from 'modules/cm/mapper/mappings';
import { CurrentIncome } from 'modules/cm/api/cases/income/types';

type ApplicantKey = 'applicant1' | 'applicant2';
type RawApplicant = NonNullable<CaseData[ApplicantKey]>;

const currentIncomeMappers = {
  mapApiToDomain(values: RawApplicant): Current {
    const { mapDate } = mappings.fromApiToDomain();
    return {
      [FIELDS.EMPLOYED_TOTAL]: values.currentIncomeEmployedTotal,
      [FIELDS.SELF_EMPLOYED_TOTAL]: values.currentIncomeSelfEmployedTotal,
      [FIELDS.OTHER_TOTAL]: values.currentIncomeOtherTotal,
      [FIELDS.CURRENT_INCOME_PENSION]: values.currentIncomePensionIncome,
      [FIELDS.TOTAL]: values.currentIncomeTotal,
      [FIELDS.BASIC_GROSS_ANNUAL_SALARY]: values.currentIncomeBasicGrossAnnualSalary,
      [FIELDS.ANNUAL_BONUS]: values.currentIncomeAnnualBonus,
      [FIELDS.HAS_ANNUAL_BONUS_GUARANTEED]: values.currentIncomeHasAnnualBonusGuaranteed,
      [FIELDS.ANNUAL_COMMISSION]: values.currentIncomeAnnualCommission,
      [FIELDS.HAS_COMMISSION_GUARANTEED]: values.currentIncomeHasCommissionGuaranteed,
      [FIELDS.ANNUAL_OVERTIME]: values.currentIncomeAnnualOvertime,
      [FIELDS.HAS_OVERTIME_GUARANTEED]: values.currentIncomeHasOvertimeGuaranteed,
      [FIELDS.ANNUAL_SHIFT_ALLOWANCE]: values.currentIncomeAnnualShiftAllowance,
      [FIELDS.HAS_SHIFT_ALLOWANCE_GUARANTEED]: values.currentIncomeHasShiftAllowanceGuaranteed,
      [FIELDS.BUSINESS_TYPE]: values.businessType,
      [FIELDS.PERCENTAGE_OF_BUSINESS_OWNED]: values.currentIncomePercentageOfBusinessOwned,
      [FIELDS.LIMITED_COMPANY_SALARY]: values.currentIncomeLimitedCompanySalary,
      [FIELDS.LIMITED_COMPANY_DIVIDENDS]: values.currentIncomeLimitedCompanyDividends,
      [FIELDS.NET_PROFIT]: values.currentIncomeNetProfit,
      [FIELDS.BUSINESS_TRADING_DATE]: mapDate(values.currentIncomeBusinessTradingDate),

      [FIELDS.INVESTMENT_PREFIX]: values.currentIncomeInvestmentIncomes.map((investment) => ({
        id: investment.id,
        [FIELDS.INVESTMENT_PORTFOLIO_VALUE]: investment.investmentPortfolioValue,
        [FIELDS.INVESTMENT_ANNUAL_INVESTMENT_INCOME]: investment.annualInvestmentIncome,
        [FIELDS.INVESTMENT_ANNUAL_DIVIDEND_INCOME]: investment.annualDividendIncome,
      })),

      [FIELDS.RENTAL_PREFIX]: values.currentIncomeRentalIncomes.map((rental) => ({
        id: rental.id,
        [FIELDS.RENTAL_GROSS_ANNUAL_RENTAL_INCOME]: rental.grossAnnualRentalIncome,
        [FIELDS.RENTAL_NO_OF_BTLS_IN_PORTFOLIO]: rental.noOfBtlsInPortfolio,
        [FIELDS.RENTAL_MORTGAGE_PAYMENT]: rental.mortgagePayment,
      })),

      [FIELDS.FOSTERING_PREFIX]: values.currentIncomeFosteringIncomes.map((fostering) => ({
        id: fostering.id,
        [FIELDS.FOSTERING_GROSS_ANNUAL_FOSTERING_INCOME]: fostering.grossAnnualFosteringIncome,
      })),

      [FIELDS.MAINTENANCE_PREFIX]: values.currentIncomeMaintenanceIncomes.map((maintenance) => ({
        id: maintenance.id,
        [FIELDS.MAINTENANCE_GROSS_ANNUAL_MAINTENANCE_INCOME]: maintenance.grossAnnualMaintenanceIncome,
        [FIELDS.HAS_MAINTENANCE_GUARANTEED_OTHER_INCOME]: maintenance.hasMaintenanceGuaranteedOtherIncome,
      })),

      [FIELDS.BENEFIT_PREFIX]: values.currentIncomeBenefitIncomes.map((benefit) => ({
        id: benefit.id,
        [FIELDS.BENEFIT_GROSS_ANNUAL_BENEFIT_INCOME]: benefit.grossAnnualBenefitIncome,
        [FIELDS.HAS_BENEFIT_GUARANTEED_OTHER_INCOME]: benefit.hasBenefitGuaranteedOtherIncome,
      })),

      [FIELDS.OTHER_PREFIX]: values.currentIncomeOtherIncomes.map((other) => ({
        id: other.id,
        [FIELDS.OTHER_GROSS_ANNUAL_OTHER_INCOME]: other.grossAnnualOtherIncome,
        [FIELDS.OTHER_HAS_BONUS_GUARANTEED_OTHER_INCOME]: other.hasBonusGuaranteedOtherIncome,
      })),
    };
  },

  mapDomainToApi(values: Current): CurrentIncome {
    const { mapDate } = mappings.fromDomainToApi();
    return {
      currentIncomeEmployedTotal: values[FIELDS.EMPLOYED_TOTAL],
      currentIncomeSelfEmployedTotal: values[FIELDS.SELF_EMPLOYED_TOTAL],
      currentIncomeOtherTotal: values[FIELDS.OTHER_TOTAL],
      currentIncomePensionIncome: values[FIELDS.CURRENT_INCOME_PENSION],
      currentIncomeTotal: values[FIELDS.TOTAL],
      currentIncomeBasicGrossAnnualSalary: values[FIELDS.BASIC_GROSS_ANNUAL_SALARY],
      currentIncomeAnnualBonus: values[FIELDS.ANNUAL_BONUS],
      currentIncomeHasAnnualBonusGuaranteed: values[FIELDS.HAS_ANNUAL_BONUS_GUARANTEED],
      currentIncomeAnnualCommission: values[FIELDS.ANNUAL_COMMISSION],
      currentIncomeHasCommissionGuaranteed: values[FIELDS.HAS_COMMISSION_GUARANTEED],
      currentIncomeAnnualOvertime: values[FIELDS.ANNUAL_OVERTIME],
      currentIncomeHasOvertimeGuaranteed: values[FIELDS.HAS_OVERTIME_GUARANTEED],
      currentIncomeAnnualShiftAllowance: values[FIELDS.ANNUAL_SHIFT_ALLOWANCE],
      currentIncomeHasShiftAllowanceGuaranteed: values[FIELDS.HAS_SHIFT_ALLOWANCE_GUARANTEED],
      currentIncomePercentageOfBusinessOwned: values[FIELDS.PERCENTAGE_OF_BUSINESS_OWNED],
      currentIncomeLimitedCompanySalary: values[FIELDS.LIMITED_COMPANY_SALARY],
      currentIncomeLimitedCompanyDividends: values[FIELDS.LIMITED_COMPANY_DIVIDENDS],
      currentIncomeNetProfit: values[FIELDS.NET_PROFIT],
      currentIncomeBusinessType: values[FIELDS.BUSINESS_TYPE] as BUSINESS_TYPES,
      currentIncomeBusinessTradingDate: mapDate(values[FIELDS.BUSINESS_TRADING_DATE]),
      currentIncomeInvestmentIncomes: values[FIELDS.INVESTMENT_PREFIX].map((investment) => ({
        id: investment.id,
        investmentPortfolioValue: investment[FIELDS.INVESTMENT_PORTFOLIO_VALUE],
        annualInvestmentIncome: investment[FIELDS.INVESTMENT_ANNUAL_INVESTMENT_INCOME],
        annualDividendIncome: investment[FIELDS.INVESTMENT_ANNUAL_DIVIDEND_INCOME],
      })),
      currentIncomeRentalIncomes: values[FIELDS.RENTAL_PREFIX].map((rental) => ({
        id: rental.id,
        grossAnnualRentalIncome: rental[FIELDS.RENTAL_GROSS_ANNUAL_RENTAL_INCOME],
        noOfBtlsInPortfolio: rental[FIELDS.RENTAL_NO_OF_BTLS_IN_PORTFOLIO],
        mortgagePayment: rental[FIELDS.RENTAL_MORTGAGE_PAYMENT],
      })),
      currentIncomeFosteringIncomes: values[FIELDS.FOSTERING_PREFIX].map((fostering) => ({
        id: fostering.id,
        grossAnnualFosteringIncome: fostering[FIELDS.FOSTERING_GROSS_ANNUAL_FOSTERING_INCOME],
      })),
      currentIncomeMaintenanceIncomes: values[FIELDS.MAINTENANCE_PREFIX].map((maintenance) => ({
        id: maintenance.id,
        grossAnnualMaintenanceIncome: maintenance[FIELDS.MAINTENANCE_GROSS_ANNUAL_MAINTENANCE_INCOME],
        hasMaintenanceGuaranteedOtherIncome: maintenance[FIELDS.HAS_MAINTENANCE_GUARANTEED_OTHER_INCOME],
      })),
      currentIncomeBenefitIncomes: values[FIELDS.BENEFIT_PREFIX].map((benefit) => ({
        id: benefit.id,
        grossAnnualBenefitIncome: benefit[FIELDS.BENEFIT_GROSS_ANNUAL_BENEFIT_INCOME],
        hasBenefitGuaranteedOtherIncome: benefit[FIELDS.HAS_BENEFIT_GUARANTEED_OTHER_INCOME],
      })),
      currentIncomeOtherIncomes: values[FIELDS.OTHER_PREFIX].map((other) => ({
        id: other.id,
        grossAnnualOtherIncome: other[FIELDS.OTHER_GROSS_ANNUAL_OTHER_INCOME],
        hasBonusGuaranteedOtherIncome: other[FIELDS.OTHER_HAS_BONUS_GUARANTEED_OTHER_INCOME],
      })),
    };
  },

  mapFieldValueToApi(field: string, value: any) {
    const { mapDate, mapNumber } = mappings.fromDomainToApi();

    const map: Map<string, (value: any) => any> = new Map<string, (value: any) => any>([
      [FIELDS.BASIC_GROSS_ANNUAL_SALARY, mapNumber],
      [FIELDS.ANNUAL_BONUS, mapNumber],
      [FIELDS.ANNUAL_COMMISSION, mapNumber],
      [FIELDS.ANNUAL_OVERTIME, mapNumber],
      [FIELDS.ANNUAL_SHIFT_ALLOWANCE, mapNumber],
      [FIELDS.LIMITED_COMPANY_SALARY, mapNumber],
      [FIELDS.LIMITED_COMPANY_DIVIDENDS, mapNumber],
      [FIELDS.PERCENTAGE_OF_BUSINESS_OWNED, mapNumber],
      [FIELDS.NET_PROFIT, mapNumber],
      [FIELDS.INVESTMENT_PORTFOLIO_VALUE, mapNumber],
      [FIELDS.INVESTMENT_ANNUAL_INVESTMENT_INCOME, mapNumber],
      [FIELDS.INVESTMENT_ANNUAL_DIVIDEND_INCOME, mapNumber],
      [FIELDS.RENTAL_GROSS_ANNUAL_RENTAL_INCOME, mapNumber],
      [FIELDS.RENTAL_NO_OF_BTLS_IN_PORTFOLIO, mapNumber],
      [FIELDS.RENTAL_MORTGAGE_PAYMENT, mapNumber],
      [FIELDS.FOSTERING_GROSS_ANNUAL_FOSTERING_INCOME, mapNumber],
      [FIELDS.MAINTENANCE_GROSS_ANNUAL_MAINTENANCE_INCOME, mapNumber],
      [FIELDS.BENEFIT_GROSS_ANNUAL_BENEFIT_INCOME, mapNumber],
      [FIELDS.OTHER_GROSS_ANNUAL_OTHER_INCOME, mapNumber],
      [FIELDS.BUSINESS_TRADING_DATE, mapDate],
    ]);

    const mapper = map.get(field);

    return mapper ? mapper(value) : value;
  },
};

export default currentIncomeMappers;
